import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ALCANCE, EQUIPO, conPunto } from '../config/alcance';
import { useReach } from '../content/hooks';
import { SITES } from '../config/sites';
import { formatoTitulo } from '../config/titulos';
import CabeceraSeccion from '../components/CabeceraSeccion';
import logoColor from '../assets/logo-color.svg';
import './fundacion.css';

/**
 * LA FUNDACIÓN.
 *
 * 🔴 TODO SU TEXTO SALE DE LA TABLA, no del código: `t('fun.…')`.
 *
 * QUÉ HABÍA ANTES. Un carrusel de tres fotos de archivo con tres frases y nada
 * más: 262 líneas para no decir ni un solo hecho — ni cuándo se creó la
 * Fundación, ni dónde está, ni quién responde por ella, ni qué ha hecho.
 *
 * EL PRINCIPIO DE ESTA PÁGINA. Una fundación se juzga por lo que se puede
 * verificar. Un RUT, una fecha de constitución y un número de registro civil
 * hacen más por la confianza de un desconocido —y de un donante futuro— que
 * diez frases inspiradoras. Y es lo coherente: una Fundación cuyo material se
 * construye sobre no inventar nada no puede presentarse con generalidades.
 *
 * ⭐⭐ MISIÓN Y PROPÓSITO NO SIGNIFICAN LO QUE SIGNIFICAN EN UN FOLLETO
 * (Faina, 26-ago). En el marco del proyecto:
 *
 *   · MISIÓN    = aquello que nos CUESTA — el aprendizaje que venimos a hacer
 *                 a esta encarnación.
 *   · PROPÓSITO = aquello que nos lleva a la FELICIDAD.
 *
 * Por eso la misión de la Fundación es «sentirnos orgullosos de ser diferentes
 * y de traer algo nuevo a la humanidad»: quienes la forman son trabajadores de
 * la luz, y lo que a un trabajador de la luz le cuesta es exactamente eso. Y el
 * propósito son las dos alas: despertar la consciencia y conservar la Tierra.
 *
 * ⚠️ NO REESCRIBIR ESAS DOS FRASES CON CRITERIO DE MARKETING. La versión
 * anterior decía «sentirnos orgullosos de lo que somos» y Claude la juzgó una
 * frase vacía: era la misión en su sentido exacto, leída en clave equivocada.
 *
 * SIN FOTOS, a propósito. Las tres eran de archivo y genéricas, y el sitio ya
 * tiene un lenguaje visual propio y mucho mejor. Una fundación seria no
 * necesita fotos de stock; necesita hechos.
 */

/* Las dos ultimas son de 28-ago y completan las cuatro primeras por donde
   estaban cojas: la 1 decia que no inventamos, pero no que ADEMAS cedemos el
   sitio a la voz del canalizador cuando la suya es mas fuerte —que es la regla
   maestra de co-autoria del proyecto—; y ninguna decia por que las piezas no
   divagan, que es lo primero que nota quien lee dos seguidas. */
const REGLAS = ['fun.regla_1', 'fun.regla_2', 'fun.regla_3', 'fun.regla_4',
  'fun.regla_5', 'fun.regla_6', 'fun.regla_7'];

/* SIN DOMICILIO (Faina, 26-ago). El de los estatutos es una direccion
   particular, y publicarla en un sitio que se lee en 55 idiomas es exponer la
   casa de alguien. Los datos que identifican a la Fundacion —RUT, registro
   civil, fecha— sirven para verificarla sin necesidad de eso. */
const FICHA = [
  ['fun.ficha_constituida', 'fun.ficha_constituida_v'],
  ['fun.ficha_inscrita', 'fun.ficha_inscrita_v'],
  ['fun.ficha_rut', 'fun.ficha_rut_v'],
  ['fun.ficha_naturaleza', 'fun.ficha_naturaleza_v'],
  ['fun.ficha_objeto', 'fun.ficha_objeto_v'],
  ['fun.ficha_marca', 'fun.ficha_marca_v']
];

function UsPage() {
  const { t } = useTranslation();

  /* 🟥 LAS CUATRO SON LO QUE HEMOS HECHO **DE VERDAD**, y las cuatro se cuentan
     solas del propio contenido (Faina, 28-ago: «es muy importante que todo lo
     que vamos a publicar sea verdad y debes tú preocuparte de esto. ES UNA
     REGLA»).

     🔴 DOS ESTABAN MAL Y SE CORRIGIERON ESE DIA:

     · «278 canalizaciones propias» era el UNIVERSO PLANIFICADO. Escritas hay
       94. Bajo un rotulo que dice «lo que hemos hecho», 278 no es una meta
       ambiciosa: es una cifra falsa. Ahora sale de `publicadas`, que cuenta el
       JSON, y subira sola segun se publiquen.
     · «33 idiomas de traduccion» tampoco: hay 25 archivos de idioma, 134 de las
       202 claves siguen en español dentro de ellos, y **ninguna canalizacion
       esta traducida todavia**. Los 33 son el objetivo (Def 35) y ese numero
       vive donde se prometen cosas, no donde se acreditan. En su lugar van las
       VOCES, que si existen y se pueden contar.

     ⚠️ Al añadir aqui una cifra, la prueba es esta: ¿se puede contar hoy en el
     propio contenido? Si hay que explicarla, no va en este bloque. */
  const reach = useReach();
  const CIFRAS = [
    [conPunto(reach.published), t('fun.hecho_propias')],
    [reach.teachings, t('fun.hecho_ensenanzas')],
    [Object.keys(SITES).length, t('fun.hecho_sitios')]
  ];

  /* Queda solo lo que depende de que alguien llegue, done o entregue tierra.
     Faina, 23-sep: «despues vemos otros dos». */
  const HOY = [
    /* Países e idiomas cubiertos BAJAN aquí (Faina, 02-oct: «pásalos para
       dónde estamos hoy»); el 23-sep habían subido a «Lo que hemos hecho».
       Son el ALCANCE DE HOY, calculado —a cuántos países se llega con los
       idiomas en que YA se pueden leer las canalizaciones—; el «1» es el
       español, y decirlo así de pequeño es lo honesto mientras el contenido no
       esté traducido. */
    [conPunto(ALCANCE.paises), t('fun.hecho_paises')],
    [reach.contentLanguages, t('fun.hecho_idiomas')],
    [conPunto(ALCANCE.suscriptores), t('fun.hecho_suscriptores')],
    [conPunto(ALCANCE.donantes), t('fun.hecho_donantes')]
    /* «Hectáreas donadas» salió el 02-oct con todo lo de la Conservación
       (Faina: «saca todo lo de la conservación, en la misma página»; Def 97,
       la conservación pasa a una etapa futura). Vuelve con ella. */
  ];

  return (
    <Box className="fun">
      {/* La cabecera es SOLO el nombre: sin rotulo que lo anuncie y sin bajada.
          Lo que decia la bajada —a que se dedica la Fundacion— pasa al pilar
          «Que hacemos», justo debajo, donde no se repite. */}
      <CabeceraSeccion titulo={t('fun.titulo')} marca logo={logoColor} />

      <Container maxWidth="lg">

        {/* ①-④ LOS CUATRO PILARES, en dos filas de dos y con la misma
            estructura (Faina, 26-ago: «replica la de misión y propósito arriba,
            con quiénes somos y qué hacemos»).

            Arriba QUIENES SOMOS y QUE HACEMOS: la identidad y el oficio.
            Abajo MISION y PROPOSITO, que se explican el uno al otro — lo que
            cuesta y lo que da felicidad.

            ⚠️ Misión y propósito NO significan aquí lo que en un folleto: ver
            la cabecera del archivo antes de reescribir esas dos frases. */}
        {[
          [
            ['fun.somos_rotulo', 'fun.somos_frase', 'fun.somos_texto'],
            ['fun.hacemos_rotulo', 'fun.hacemos_frase', 'fun.hacemos_texto']
          ],
          [
            ['fun.mision_rotulo', 'fun.mision_frase', 'fun.mision_texto'],
            ['fun.proposito_rotulo', 'fun.proposito_frase', 'fun.proposito_texto']
          ]
        ].map((fila, i) => (
          <Box className="fun-seccion fun-dos" key={i}>
            {fila.map(([rotulo, frase, texto]) => (
              <Box className="fun-pilar" key={rotulo}>
                <Typography className="fun-rotulo">{t(rotulo)}</Typography>
                <Typography className="fun-frase">{t(frase)}</Typography>
                <Typography className="fun-parrafo">{t(texto)}</Typography>
              </Box>
            ))}
          </Box>
        ))}

        {/* ④ CUÁNDO SE FORMÓ — el bloque verificable.
            🔴 VA ANTES DE «LO QUE HEMOS HECHO» (Faina, 1-sep). Estaba al final,
            entre la Conservación y el cierre, y ahí llegaba tarde: quien lee
            una fundación que no conoce necesita saber primero QUE EXISTE —con
            su fecha, su registro y su RUT— y solo despues creerle lo que dice
            que ha hecho. Puesta detras, la ficha se leia como una nota legal;
            puesta delante, es lo que sostiene todas las cifras que vienen. */}
        <Box className="fun-seccion">
          <Typography className="fun-rotulo">{t('fun.ficha_rotulo')}</Typography>
          <Box component="dl" className="fun-ficha">
            {FICHA.map(([campo, valor]) => (
              <React.Fragment key={campo}>
                <Box component="dt" className="fun-ficha-campo">{t(campo)}</Box>
                <Box component="dd" className="fun-ficha-valor">{t(valor)}</Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* ⑤ LO QUE HEMOS HECHO — donde la Fundación deja de prometer */}
        <Box className="fun-seccion">
          <Typography className="fun-rotulo">{t('fun.hecho_rotulo')}</Typography>
          <Box className="fun-cifras">
            {CIFRAS.map(([n, que]) => (
              <Box className="fun-cifra" key={que}>
                <span className="fun-cifra-n">{n}</span>
                <span className="fun-cifra-que">{formatoTitulo(que)}</span>
              </Box>
            ))}
          </Box>
          {/* La frase va DEBAJO de los numeros (Faina, 26-ago). Encima era una
              promesa; debajo es la conclusion de lo que el lector acaba de ver:
              primero la evidencia, despues lo que significa. */}

        </Box>

        {/* ⑥ DÓNDE ESTAMOS HOY — mismo formato que «Lo que hemos hecho»
            (Faina, 26-ago). Separado a proposito: uno cuenta el trabajo hecho y
            este el punto de partida, y mezclarlos en una sola parrilla los hacia
            parecer lo mismo.
            🔴 SUS DOS CIFRAS SON PROVISIONALES — el porque y los valores, en
            config/alcance.js §PROVISIONALES. Las pruebas avisan en cada pasada
            mientras siga asi. */}
        <Box className="fun-seccion">
          <Typography className="fun-rotulo">{t('fun.hoy_rotulo')}</Typography>
          <Box className="fun-cifras">
            {HOY.map(([n, que]) => (
              <Box className="fun-cifra" key={que}>
                <span className="fun-cifra-n">{n}</span>
                <span className="fun-cifra-que">{formatoTitulo(que)}</span>
              </Box>
            ))}
          </Box>
        </Box>

        {/* CUÁNTOS TRABAJAMOS (Faina, 1-sep) — «lo que hay sobre lo que se
            busca»: 3/4, 1/33, 0/0, 2/195. El «0/0» de los empleados es una
            decisión declarada, no un hueco por llenar.

            MISMO FORMATO que «Dónde estamos hoy» (Faina, 1-sep: «sigue el mismo
            formato de las tablas de arriba, borra la cháchara»): cifra centrada
            y su etiqueta debajo, sin párrafo de entrada ni pie explicativo. Si
            una casilla necesita explicarse, es que la cifra está mal elegida.

            Las cifras viven en `config/alcance.js` → EQUIPO, nunca aquí. */}
        <Box className="fun-seccion">
          <Typography className="fun-rotulo">{t('fun.directorio_rotulo')}</Typography>
          <Box className="fun-cifras">
            {EQUIPO.map(({ clave, hay, meta }) => (
              <Box className="fun-cifra" key={clave}>
                <span className="fun-cifra-n">
                  {hay}<span className="fun-cifra-meta">/{meta}</span>
                </span>
                <span className="fun-cifra-que">{t(`fun.equipo.${clave}.titulo`)}</span>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ⑦ CÓMO TRABAJAMOS
            `id` + `.ancla` para que cada canalización pueda mandar aquí desde
            «Canalizado por Faina · Cómo trabajamos» (11-sep). La duda «¿esto es
            inventado?» le nace al lector delante de la pieza, no en esta
            página; el enlace la trae hasta las siete reglas en un clic. */}
        <Box className="fun-seccion ancla" id="como-trabajamos">
          <Typography className="fun-rotulo">{t('fun.reglas_rotulo')}</Typography>
          <Box className="fun-reglas">
            {REGLAS.map((clave) => (
              /* Faina, 20-sep: la primera frase de cada regla —hasta el primer
                 punto— va sola en la primera linea, y el resto debajo. Se parte
                 al pintar, no en el texto: la tabla guarda la regla entera. */
              <Typography className="fun-regla" key={clave}>
                {(() => {
                  const texto = t(clave);
                  const corte = texto.indexOf('. ');
                  if (corte < 0) return texto;
                  return (
                    <>
                      <span className="fun-regla-primera">{texto.slice(0, corte + 1)}</span>
                      {texto.slice(corte + 2)}
                    </>
                  );
                })()}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* ⑧ LA CONSERVACIÓN — RETIRADA (Faina, 02-oct: «saca todo lo de la
            conservación, en la misma página»; Def 97: pasa a una etapa futura y
            no se publica). El bloque —rótulo, texto y el enlace a donar un
            terreno— vive en el respaldo UsPage.js.bak_2026-10-02 y sus textos
            siguen en la tabla (fun.conservacion_*): vuelve cuando vuelva la
            conservación. */}

        {/* ⑨ CÓMO NOS FINANCIAMOS — en cuadro, como la Conservación (Faina,
            1-sep: «usa el mismo formato de la conservación, abajo dos
            viñetas»).

            Las dos secciones cierran la página y hablan de lo mismo —de qué
            vive esto—, así que van iguales: un cuadro con el lado naranja y
            dentro el texto. La diferencia es que aquí el cuadro lleva además
            las dos puertas.

            Se llamaba «Cómo se sostiene» y traía tres vías; los terrenos son
            otra cosa —no financian, conservan— y se fueron al cuadro de la
            Conservación, que es donde se explican.

            🔴 LA DEL DINERO SIGUE SIN LLEGAR A LOS DATOS BANCARIOS, QUE ES
            DONDE FAINA LA QUIERE: la Fundación no tiene ninguna cuenta
            publicada —buscada en la web y en todo el proyecto, no existe— y una
            cuenta bancaria no se inventa ni se deduce. Va al formulario de
            donación, que es lo que el sitio sabe hacer hoy. En cuanto lleguen
            los datos se publican y este enlace apunta ahí. */}
        <Box className="fun-seccion fun-cierre">
          <Typography className="fun-rotulo">{t('fun.sostiene_rotulo')}</Typography>
          <Box className="fun-reglas">
            <Box className="fun-regla">
              {/* SIN FRASE DE ENTRADA (Faina, 1-sep: «bórralo»). Decia «leer una
                  canalizacion entera es gratis y siempre lo sera; nos
                  financiamos de dos maneras» — lo primero ya lo dicen los
                  planes de la portada, y lo segundo lo dice el rotulo de la
                  seccion. Las dos puertas se explican solas. */}
              <Box component="ul" className="fun-sostiene">
                <Box component="li">
                  <RouterLink to="/#planes">{t('fun.sostiene_suscripciones')}</RouterLink>
                </Box>
                <Box component="li">
                  <RouterLink to="/contacto?motivo=donar">{t('fun.sostiene_dinero')}</RouterLink>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default UsPage;
